import { PencilIcon } from "lucide-react"
import { Header } from "../Components/Header/Header"
import { ColumnDef, DataTable } from "../Components/Table/DataTable"
import useGetCustomData from "../Hooks/useGetCustomData"
import { AddButton, CloseButton, EditButton, ModalHeader, ModalOverlay, PageContainer } from "../Styles/AppStyles"
import { AssetCategory, USER_ROLE } from "../Types"
import { useEffect, useMemo, useState } from "react"
import { ModalContent } from "./UsersPage"
import { useSnackbar } from "../Context/SnackbarProvider"
import { decode } from "../Services/DecodeToken"

import { AssetCategoryForm } from "./Assets/AssetCategoryForm"
import apiClientFormData from "../Services/ApiClientFormData"
import { CompanySelect } from "../Components/Select/CompanySelect"
import { useColumnSearches } from "../Hooks/useColumnSearch"
import { SearchableHeader } from "../Components/Search/SearchableHeader"




export const AssetCategoryPage = () => {

    const [showAddModal, setShowAddModal] = useState(false);
    const [assetCategoryToEdit, setAssetCategoryToEdit] = useState<AssetCategory | null>(null);

    const handleCloseModal = () => {
        setShowAddModal(false);
        setAssetCategoryToEdit(null);
    };

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [sortField, setSortField] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    const searchFields = ['category_name', 'description', 'customer_name', 'location_name'];
    const { columnSearches, setSearch, clearSearch } = useColumnSearches(searchFields);

    const handleColumnSearch = (field: string, val: string) => {
        setSearch(field, val);
        setPage(0);
    };

    const handleClearColumnSearch = (field: string) => {
        clearSearch(field);
        setPage(0);
    };

    const handleSortChange = (field: string, order: 'asc' | 'desc' | null) => {
        setSortField(field);
        setSortOrder(order ?? 'asc');
        setPage(0);
    };

    const [CID] = useGetCustomData('/api/company/userregister');
    const [selectedCompany, setSelectedCompany] = useState<string>('');

    const token: any = decode();
    const { showSnackbar } = useSnackbar();

    useEffect(() => {
        if (
            token.roles.id !== USER_ROLE.ADMIN_ABATIX &&
            CID.length > 0 &&
            !selectedCompany
        ) {
            setSelectedCompany(CID[0].OVID);
        }
    }, [CID, selectedCompany]);

    const baseUrl = useMemo(() => {
        const params = new URLSearchParams();
        if (selectedCompany && selectedCompany !== '') {
            params.set('cid', selectedCompany);
        }
        Object.entries(columnSearches).forEach(([field, value]) => {
            if (value.trim()) params.set(field, value.trim());
        });
        if (sortField) {
            params.set('sort_field', sortField);
            params.set('sort_order', sortOrder);
        }
        return `/api/assets/category?${params.toString()}`;
    }, [selectedCompany, CID, columnSearches, sortField, sortOrder]);

    const [assetCategory, loadingAssetCategory, , refetch] = useGetCustomData(baseUrl);

    const assetCategoryList = Array.isArray(assetCategory)
        ? assetCategory
        : (assetCategory?.data ?? []);
    const totalRows = Array.isArray(assetCategory)
        ? assetCategory.length
        : (assetCategory?.totalCount ?? assetCategoryList.length);

    const assetCategoryColumns: ColumnDef<any>[] = [
        {
            field: 'customer_name',
            headerName: 'Account',
            width: 200,
            sortable: true,
            defaultFirst: true,
            renderHeader: () => (
                <SearchableHeader
                    field="customer_name"
                    headerName="Account"
                    searchValue={columnSearches.customer_name}
                    onSearchChange={handleColumnSearch}
                    onClearSearch={handleClearColumnSearch}
                />
            ),
        },
        {
            field: 'location_name',
            headerName: 'Location',
            width: 160,
            sortable: true,
            renderHeader: () => (
                <SearchableHeader
                    field="location_name"
                    headerName="Location"
                    searchValue={columnSearches.location_name}
                    onSearchChange={handleColumnSearch}
                    onClearSearch={handleClearColumnSearch}
                />
            ),
        },
        {
            field: 'category_name',
            headerName: 'Category Name',
            width: 200,
            sortable: true,
            renderHeader: () => (
                <SearchableHeader
                    field="category_name"
                    headerName="Category Name"
                    searchValue={columnSearches.category_name}
                    onSearchChange={handleColumnSearch}
                    onClearSearch={handleClearColumnSearch}
                />
            ),
        },
        {
            field: 'description',
            headerName: 'Description',
            width: 250,
            sortable: true,
            renderHeader: () => (
                <SearchableHeader
                    field="description"
                    headerName="Description"
                    searchValue={columnSearches.description}
                    onSearchChange={handleColumnSearch}
                    onClearSearch={handleClearColumnSearch}
                />
            ),
        },
        { field: 'active_flag', headerName: 'Active', width: 100, sortable: true, renderCell: (params) => params.value ? 'Y' : 'N' },
        { field: 'full_name', headerName: 'Created By', width: 120, sortable: true },
        {
            field: 'actions',
            headerName: '',
            width: 100,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <div style={{ display: 'flex', gap: 5, alignItems: 'center', justifyContent: "center", marginTop: 12 }}>
                    <EditButton onClick={() => {
                        setAssetCategoryToEdit(params.row);
                        setShowAddModal(true);
                    }}>
                        <PencilIcon />
                    </EditButton>
                </div>
            ),
        },
    ];

    const handleSubmit = async (formData: any) => {
        const isNew = !assetCategoryToEdit;

        const payload: any = {
            category_name: formData.category_name,
            description: formData.description || '',
            active_flag: formData.active_flag ?? 1,
            customer_id: formData.ovid || formData.customer_id || null,
            location_id: formData.location_id ? Number(formData.location_id) : null,
        };

        const url = isNew ? '/api/assets/category' : `/api/assets/category/${assetCategoryToEdit.id}`;
        const method = isNew ? 'POST' : 'PUT';

        const form = new FormData();
        form.append('data', JSON.stringify(payload));
        if (formData.image) {
            form.append('image', formData.image);
        }

        try {
            const response = await apiClientFormData(url, { method, body: form });

            if (!response.ok) {
                const errorData = await response.json();
                showSnackbar(errorData.error || 'Failed to save category', 'error', 'Something went wrong!');
                return;
            }

            const savedCategory = await response.json();
            showSnackbar(
                `${savedCategory.category_name} was successfully ${isNew ? 'added' : 'updated'}.`,
                'success',
                isNew ? 'Successfully Added!' : 'Successfully Updated!'
            );
            refetch();
            setShowAddModal(false);
        } catch (err: any) {
            showSnackbar(err.message, 'error', 'Something went wrong!');
        }
    };

    return (
        <PageContainer>
            <Header title="Asset Category" count={totalRows} hideCount={false} >
                <AddButton onClick={() => setShowAddModal(true)}>+</AddButton>
            </Header>

            {showAddModal && (
                <ModalOverlay onClick={() => setShowAddModal(false)}>
                    <ModalContent onClick={e => e.stopPropagation()}>
                        <CloseButton onClick={handleCloseModal}>×</CloseButton>
                        <ModalHeader>{assetCategoryToEdit ? "Edit" : "Add"} Asset Category</ModalHeader>
                        <AssetCategoryForm onSubmit={handleSubmit} onCancel={handleCloseModal} defaultValues={assetCategoryToEdit} />
                    </ModalContent>
                </ModalOverlay>
            )}

            <DataTable
                columns={assetCategoryColumns}
                data={assetCategoryList}
                loading={loadingAssetCategory}
                pageKey="asset-category"
                idField="id"
                hideColumnFilters={true}
                hideSearchBar={true}
                serverSideSort={true}
                onSortModelChange={handleSortChange}
            >
                <CompanySelect
                    companies={CID}
                    selectedCompany={selectedCompany}
                    onChange={setSelectedCompany}
                />
            </DataTable>
        </PageContainer>
    );
}
